import { Link } from 'react-router-dom';
import { ArrowRight, FileEdit, Target, TrendingUp, Eye, CheckCircle, Users } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1762429648253-b8bc921c8b30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eSUyMGJ1c2luZXNzfGVufDB8fHx8MTc3MjQ0MDI1Nnww&ixlib=rb-4.1.0&q=85';
const CPM_IMG = 'https://images.unsplash.com/photo-1758691737584-a8f17fb34475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtd29yayUyMGRpdmVyc2UlMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc3MjQ0MDI1NXww&ixlib=rb-4.1.0&q=85';

const features = [
  { icon: FileEdit, title: 'Resume Optimization', desc: 'Professional restructuring and formatting of candidate resumes to meet enterprise submission standards and highlight key competencies.' },
  { icon: Target, title: 'Skill Mapping', desc: 'Precise alignment of candidate skills with client job descriptions, ensuring maximum relevance and interview conversion.' },
  { icon: Eye, title: 'Profile Presentation', desc: 'Curated candidate profiles that showcase experience, certifications, and achievements in a compelling, client-ready format.' },
  { icon: TrendingUp, title: 'Market Positioning', desc: 'Strategic positioning of candidate profiles based on current market demand, salary benchmarks, and industry trends.' },
  { icon: CheckCircle, title: 'Quality Assurance', desc: 'Multi-stage review process ensuring accuracy, consistency, and completeness of every candidate profile submitted.' },
  { icon: Users, title: 'Client Matching', desc: 'Intelligent matching algorithms combined with recruiter expertise to connect profiles with the most suitable opportunities.' },
];

const CandidateProfileMarketing = () => {
  return (
    <div data-testid="candidate-profile-marketing-page">
      <PageHeader caption="Candidates" title="Candidate Profile Marketing" subtitle="Professional profile presentation to maximize placement success." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={CPM_IMG} alt="Profile marketing process" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <p className="sol-caption mb-4">Profile Marketing</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Profile, Professionally Presented</h2>
              <p className="text-[#525252] leading-relaxed mb-4">In today's competitive job market, how your profile is presented can be the difference between getting noticed and getting passed over. Solnity's Candidate Profile Marketing service ensures your skills, experience, and achievements are showcased in the most impactful way possible.</p>
              <p className="text-[#525252] leading-relaxed mb-8">Our team of recruitment specialists and content professionals work together to create compelling, client-ready profiles that significantly increase interview conversion rates.</p>
              <Link to="/join-talent-pool" className="sol-btn-primary text-sm">Join Talent Pool <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">What We Offer</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Marketing Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="sol-service-card bg-white group">
                <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] mb-5 group-hover:border-black group-hover:bg-black transition-all duration-300">
                  <f.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Process</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[{ step: '01', title: 'Profile Submission', desc: 'Submit your resume and career details through our talent pool portal.' }, { step: '02', title: 'Assessment', desc: 'Our team reviews your background, skills, and career objectives.' }, { step: '03', title: 'Profile Enhancement', desc: 'We optimize and professionally format your profile for client submissions.' }, { step: '04', title: 'Active Marketing', desc: 'Your enhanced profile is marketed to relevant enterprise opportunities.' }].map((s) => (
              <div key={s.step} className="sol-step-card">
                <div className="step-number mb-3">{s.step}</div>
                <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h4>
                <p className="text-xs text-[#525252] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Get Your Profile Noticed</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">Join our talent pool and let us market your professional profile to top enterprise clients.</p>
          <Link to="/join-talent-pool" data-testid="cpm-cta-join" className="sol-btn-inverse text-sm">Join Talent Pool <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
};

export default CandidateProfileMarketing;
